import { useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { useImportJobPolling } from "@/hooks/useImportJobPolling";
import { JOB_STATUS, ROUTES } from "@/types";
import { RecipeService } from "@/lib/recipeService";
import { toaster } from "@/lib/toaster";
import { trackRecipeImport } from "@/lib/analytics";

interface UseImportJobFlowOptions {
  onSuccess?: (recipeId: string) => void;
  onError?: (error: string) => void;
  errorTitle?: string;
}

export function useImportJobFlow(options: UseImportJobFlowOptions = {}) {
  const [, setLocation] = useLocation();
  const toastIdRef = useRef<string | undefined>(undefined);
  const { isPolling, status, error: pollingError, recipeId, scrapeMethod, startPolling, stopPolling } = useImportJobPolling();

  const { errorTitle = "Import failed" } = options;

  // Keep callback refs up-to-date on every render so effects never need them as deps
  const onSuccessRef = useRef(options.onSuccess);
  onSuccessRef.current = options.onSuccess;

  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;

  // Handle successful import completion
  useEffect(() => {
    if (status !== JOB_STATUS.COMPLETE || !recipeId) return;

    if (toastIdRef.current) {
      toaster.dismiss(toastIdRef.current);
      toastIdRef.current = undefined;
    }

    trackRecipeImport(scrapeMethod ?? undefined);

    let cancelled = false;
    void (async () => {
      try {
        await RecipeService.loadOne(recipeId);
      } catch {
        // Navigate anyway.
      }
      if (cancelled) return;

      if (onSuccessRef.current) {
        onSuccessRef.current(recipeId);
      } else {
        setLocation(ROUTES.RECIPE(recipeId));
      }

      if (scrapeMethod != null && !scrapeMethod.includes('json-ld')) {
        toaster.create({
          title: "Recipe imported successfully",
          description: "The recipe details were interpreted from unstructured content, so some details may need a quick check.",
          type: "warning",
          duration: 8000,
          closable: true,
        });
      }
    })();

    return () => { cancelled = true; };
  }, [status, recipeId, scrapeMethod, setLocation]);

  // Handle import failure
  useEffect(() => {
    if (status === JOB_STATUS.FAILED && pollingError) {
      if (toastIdRef.current) {
        toaster.dismiss(toastIdRef.current);
      }

      toastIdRef.current = toaster.create({
        title: errorTitle,
        description: pollingError,
        type: "error",
      });

      onErrorRef.current?.(pollingError);
    }
  }, [status, pollingError, errorTitle]);

  // Stop polling when user navigates away
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const closeExistingToast = () => {
    if (toastIdRef.current) {
      toaster.dismiss(toastIdRef.current);
      toastIdRef.current = undefined;
    }
  };

  return {
    isPolling,
    status,
    error: pollingError,
    recipeId,
    startPolling,
    stopPolling,
    closeExistingToast,
  };
}
